'use client';

import { RealtimeContext } from '@upstash/realtime/client';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuthStore } from '../../auth';

/**
 * Realtime Provider for Linkode
 *
 * Provides SSE connection management for the Upstash Realtime SDK with a patched
 * URL builder that preserves existing query parameters (token) while appending
 * channel and last_ack params. The upstream SDK always appends `?channel=...`
 * which breaks when the base URL already contains query params; this provider
 * uses the URL API to merge params safely and avoid the double-`?` bug.
 *
 * @module features/collab/transport/RealtimeProvider
 * @see SPEC-007-6 Requirements 6.1-6.7
 */

// ============================================================================
// Types
// ============================================================================

interface RealtimeProviderProps {
  children: ReactNode;
}

type Subscriber = {
  channels: Set<string>;
  cb: (payload: unknown) => void;
};

type ConnectionStatus = 'connected' | 'disconnected' | 'error' | 'connecting';
type SystemEvent =
  | { type: 'connected'; channel: string; cursor?: string }
  | { type: 'reconnect'; timestamp: number }
  | { type: 'error'; error: string }
  | { type: 'disconnected'; channels: string[] }
  | { type: 'ping'; timestamp: number };

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get API base URL from environment variable or default
 *
 * Priority:
 * 1. VITE_API_URL environment variable
 * 2. Empty string (relative path, same origin)
 */
function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return '';
}

function buildRealtimeUrl(sessionToken: string | null): string {
  const base = `${getApiBaseUrl()}/realtime`;
  try {
    const url = new URL(
      base,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    if (sessionToken) {
      url.searchParams.set('token', sessionToken);
    }
    return url.toString();
  } catch {
    // Fallback (should not happen in browser runtime)
    return sessionToken ? `${base}?token=${encodeURIComponent(sessionToken)}` : base;
  }
}

function isSystemEvent(payload: unknown): payload is SystemEvent {
  if (!payload || typeof payload !== 'object') return false;
  const evt = payload as Record<string, unknown>;
  switch (evt.type) {
    case 'connected':
      return typeof evt.channel === 'string';
    case 'reconnect':
      return typeof evt.timestamp === 'number';
    case 'error':
      return typeof evt.error === 'string';
    case 'disconnected':
      return Array.isArray(evt.channels);
    case 'ping':
      return typeof evt.timestamp === 'number';
    default:
      return false;
  }
}

function isUserEvent(
  payload: unknown
): payload is { id: string; channel: string; event: string; data: unknown } {
  if (!payload || typeof payload !== 'object') return false;
  const evt = payload as Record<string, unknown>;
  return (
    typeof evt.id === 'string' &&
    typeof evt.channel === 'string' &&
    typeof evt.event === 'string' &&
    'data' in evt
  );
}

// ============================================================================
// Component
// ============================================================================

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const sessionToken = useAuthStore((state) => state.sessionToken);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const shouldEnable = mounted && hasHydrated && isAuthenticated && !!sessionToken;

  const realtimeUrl = useMemo(() => buildRealtimeUrl(sessionToken), [sessionToken]);
  const apiConfig = useMemo(
    () => ({
      url: realtimeUrl,
      withCredentials: true,
    }),
    [realtimeUrl]
  );

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const localSubsRef = useRef<Map<string, Subscriber>>(new Map());
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const lastAckRef = useRef<Map<string, string>>(new Map());
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectRef = useRef<() => void>(() => {});

  const getAllNeededChannels = useCallback((): Set<string> => {
    if (!shouldEnable) return new Set();
    const channels = new Set<string>();
    localSubsRef.current.forEach((sub) => {
      sub.channels.forEach((ch) => channels.add(ch));
    });
    return channels;
  }, [shouldEnable]);

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = null;
    }
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    setStatus('disconnected');
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const resetPingTimeout = useCallback(() => {
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
    }
    pingTimeoutRef.current = setTimeout(() => {
      console.warn('Connection timed out, reconnecting...');
      connectRef.current();
    }, 75_000);
  }, []);

  const handleMessage = useCallback((payload: unknown) => {
    if (isSystemEvent(payload)) {
      const event = payload;
      if (event.type === 'reconnect') {
        connectRef.current();
      }
      if (event.type === 'connected' && event.cursor && event.channel) {
        lastAckRef.current.set(event.channel, event.cursor);
      }
      return;
    }

    if (isUserEvent(payload)) {
      const { channel, id } = payload;
      lastAckRef.current.set(channel, id);
      localSubsRef.current.forEach((sub) => {
        if (sub.channels.has(channel)) {
          sub.cb(payload);
        }
      });
    }
  }, []);

  const connect = useCallback(
    (opts?: { replayEventsSince?: number }) => {
      const { replayEventsSince = Date.now() } = opts ?? {};
      const channels = Array.from(getAllNeededChannels());
      if (!shouldEnable || channels.length === 0) return;

      if (reconnectAttemptsRef.current >= 3) {
        console.warn('Max reconnection attempts reached.');
        setStatus('error');
        return;
      }

      cleanup();
      setStatus('connecting');
      try {
        const url = new URL(
          apiConfig.url,
          typeof window !== 'undefined' ? window.location.origin : undefined
        );
        channels.forEach((ch) => url.searchParams.append('channel', ch));
        channels.forEach((ch) => {
          const lastAck = lastAckRef.current.get(ch) ?? String(replayEventsSince);
          url.searchParams.append(`last_ack_${ch}`, lastAck);
        });

        const eventSource = new EventSource(url.toString(), {
          withCredentials: apiConfig.withCredentials ?? false,
        });
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          reconnectAttemptsRef.current = 0;
          setStatus('connected');
          resetPingTimeout();
        };

        eventSource.onmessage = (evt) => {
          try {
            const payload = JSON.parse(evt.data);
            resetPingTimeout();
            handleMessage(payload);
          } catch (error) {
            console.warn('Error parsing message:', error);
          }
        };

        eventSource.onerror = () => {
          if (eventSource !== eventSourceRef.current) return;
          const readyState = eventSourceRef.current?.readyState;
          if (readyState === EventSource.CONNECTING) return;
          setStatus('disconnected');
          if (reconnectAttemptsRef.current < 3) {
            reconnectAttemptsRef.current++;
            reconnectTimeoutRef.current = setTimeout(
              () => {
                connectRef.current();
              },
              Math.min(1_000 * reconnectAttemptsRef.current, 10_000)
            );
          } else {
            setStatus('error');
          }
        };
      } catch (error) {
        console.warn('Failed to establish SSE connection:', error);
        setStatus('error');
      }
    },
    [
      apiConfig.url,
      apiConfig.withCredentials,
      cleanup,
      getAllNeededChannels,
      handleMessage,
      resetPingTimeout,
      shouldEnable,
    ]
  );

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const debouncedConnect = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      connectRef.current();
      debounceTimeoutRef.current = null;
    }, 25);
  }, []);

  const register = useCallback(
    (id: string, channels: string[], cb: (payload: unknown) => void) => {
      localSubsRef.current.set(id, { channels: new Set(channels), cb });
      debouncedConnect();
    },
    [debouncedConnect]
  );

  const unregister = useCallback(
    (id: string) => {
      const channels = Array.from(localSubsRef.current.get(id)?.channels ?? []);
      channels.forEach((channel) => {
        lastAckRef.current.delete(channel);
      });
      localSubsRef.current.delete(id);
      if (localSubsRef.current.size === 0) {
        cleanup();
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        return;
      }
      debouncedConnect();
    },
    [cleanup, debouncedConnect]
  );

  useEffect(() => {
    if (!shouldEnable) {
      cleanup();
      lastAckRef.current.clear();
    }
  }, [cleanup, shouldEnable]);

  return (
    <RealtimeContext.Provider value={{ status, register, unregister }}>
      {children}
    </RealtimeContext.Provider>
  );
}
